import type { EditorView } from "@tiptap/pm/view";
import { type Editor as TiptapEditor,useEditor } from "@tiptap/react";
import { useEffect, useMemo, useRef } from "react";

import { createFormulaEditorExtensions } from "../editor/create-editor";
import type { ResourceOptionsForPruneRef } from "../paragraph-resource-meta/resource-slots-prune";
import type { ReactionResourceOption, SuggestionState } from "../suggestion/suggestion-types";

export interface UseFormulaEditorParams {
  value?: string;
  onChange?: (html: string) => void;
  onSuggestionUpdate?: (state: SuggestionState | null) => void;
  onSuggestionKeyDown?: (view: EditorView, event: KeyboardEvent) => boolean;
  getContainerRect?: () => DOMRect | null;
  resourceOptionsForPrune?: ReactionResourceOption[];
  resourceOptionsRef?: ResourceOptionsForPruneRef;
  preserveResourceSlotsOnClipboard?: boolean;
  onPastedResourceSlots?: (slots: string[]) => void;
}

export function useFormulaEditor({
	value,
	onChange,
	onSuggestionUpdate,
	onSuggestionKeyDown,
	getContainerRect,
	resourceOptionsForPrune,
	resourceOptionsRef,
	preserveResourceSlotsOnClipboard,
	onPastedResourceSlots,
}: UseFormulaEditorParams): TiptapEditor | null {
	const lastValueFromEditor = useRef<string | undefined>(value);
	const onPastedResourceSlotsRef = useRef(onPastedResourceSlots);
	useEffect(() => {
		onPastedResourceSlotsRef.current = onPastedResourceSlots;
	}, [onPastedResourceSlots]);
	const internalResourceOptionsRef = useRef<ReactionResourceOption[]>(
		resourceOptionsRef?.current ?? resourceOptionsForPrune ?? [],
	);
	const resolvedResourceOptionsRef = resourceOptionsRef ?? internalResourceOptionsRef;

	const extensions = useMemo(
		() =>
			createFormulaEditorExtensions({
				onSuggestionUpdate,
				onSuggestionKeyDown,
				getContainerRect,
				resourceOptionsRef: resolvedResourceOptionsRef,
				preserveResourceSlotsOnClipboard,
				onPastedResourceSlots: (slots) => {
					onPastedResourceSlotsRef.current?.(slots);
				},
			}),
		[
			onSuggestionUpdate,
			onSuggestionKeyDown,
			getContainerRect,
			resolvedResourceOptionsRef,
			preserveResourceSlotsOnClipboard,
		]
	);

	useEffect(() => {
		if (resourceOptionsRef) return;
		internalResourceOptionsRef.current = resourceOptionsForPrune ?? [];
	}, [resourceOptionsForPrune, resourceOptionsRef]);

	const editor = useEditor({
		content: value ?? "",
		extensions,
		onUpdate({ editor }) {
			const html = editor.getHTML();
			lastValueFromEditor.current = html;
			onChange?.(html);
		},
	});

	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		if (value === undefined) return;
		if (value === lastValueFromEditor.current) return;

		lastValueFromEditor.current = value;
		editor.commands.setContent(value, false);
	}, [editor, value]);

	return editor;
}
